import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { SITE, SPONSOR } from "@/lib/site";

const URL = `${SITE.url}/ownership-and-funding`;
const LAST_UPDATED = "August 2026";

export const Route = createFileRoute("/ownership-and-funding")({
  head: () => ({
    meta: [
      { title: `Ownership & funding — ${SITE.name}` },
      { name: "description", content: "Map Observer is founded and funded by GBPly. Full disclosure of who pays for this newsroom, what the money buys, and the editorial firewall that keeps our reporting independent." },
      { property: "og:title", content: "Ownership & funding" },
      { property: "og:description", content: "Map Observer is founded and funded by GBPly. Here is exactly what that does — and does not — mean for our reporting." },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-5 sm:px-8 py-12">
        <p className="eyebrow">Disclosure</p>
        <h1 className="headline-display text-4xl mt-3">Ownership &amp; funding</h1>
        <p className="mt-4 text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>

        <section className="mt-10 space-y-4 text-sm leading-relaxed">
          <h2 className="headline-display text-2xl">The short version</h2>
          <p className="text-base">
            Map Observer was founded and is funded by{" "}
            <a href={SPONSOR.url} rel={SPONSOR.rel} className="font-medium text-ochre-deep underline underline-offset-4 hover:text-accent">GBPly</a>
            {" "}({SPONSOR.tagline}), a software platform for managing Google Business Profiles.
            GBPly pays for this newsroom. GBPly does not decide what it publishes.
          </p>

          <h2 className="headline-display text-2xl mt-8">Ownership</h2>
          <p>Map Observer is owned by GBPly. This publication would not exist without them — they created it, they resource it, and they carry its costs.</p>
          <p>It is not affiliated with, endorsed by, or operated on behalf of Google or Alphabet. Nothing we publish should be read as a statement from Google.</p>

          <h2 className="headline-display text-2xl mt-8">Funding</h2>
          <p>Map Observer is currently supported entirely by GBPly. Specifically, that funding covers:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Editorial production — reporting, editing, and fact-checking</li>
            <li>Hosting, source monitoring, and the AI tooling described in our editorial standards</li>
            <li>The free reader products: the daily briefing, newsletter, trackers, reference tools, public API, and embeds</li>
          </ul>
          <p>We take no money from readers, run no paywall, and sell no reader data. If we later accept third-party sponsorship, paid placements, or affiliate revenue, those relationships will be disclosed on this page and labelled visibly wherever they appear.</p>

          <h2 className="headline-display text-2xl mt-8">The editorial firewall</h2>
          <p>Being funded by a company in the industry we cover is a real conflict of interest, and the honest response is to be specific about how it is managed rather than to claim it away. Our commitments:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>GBPly does not select, commission, or kill stories.</li>
            <li>GBPly does not review, approve, or amend copy before publication, and does not see our reporting ahead of readers.</li>
            <li>GBPly receives no more favourable coverage than any other vendor, and no influence over how we cover their competitors.</li>
            <li>Where GBPly staff contribute subject-matter expertise, they are treated as a named source and credited as such — not as an unattributed editor.</li>
          </ul>
          <p>Editorial judgement sits with the named editor on the <Link to="/newsroom" className="text-accent underline">masthead</Link>. If we ever could not report a story honestly because of who funds us, we would say so in place of the story.</p>

          <h2 className="headline-display text-2xl mt-8">How you can check</h2>
          <p>Every article that mentions GBPly or a direct competitor carries an inline disclosure at the point of mention. Sponsor placements are labelled &ldquo;Sponsored&rdquo; and kept visually separate from editorial content. Links to GBPly carry the <code>sponsored</code> link attribution that search engines expect for a funded relationship.</p>
          <p>More detail on the relationship is on our <Link to="/sponsors/gbply" className="text-accent underline">sponsor page</Link>. See also our <Link to="/editorial-standards" className="text-accent underline">editorial standards</Link>, <Link to="/ethics" className="text-accent underline">ethics policy</Link>, and <Link to="/corrections" className="text-accent underline">corrections policy</Link>.</p>

          <h2 className="headline-display text-2xl mt-8">No undisclosed commercial relationships</h2>
          <p>We do not accept payment, gifts, equity, or other compensation in exchange for editorial coverage. We do not coordinate stories with vendors or PR teams. Press releases are treated as one source among many and never republished verbatim.</p>

          <h2 className="headline-display text-2xl mt-8">AI vendors</h2>
          <p>Our editorial pipeline uses commercial AI APIs (currently including the Google Gemini family via the Lovable AI Gateway and Firecrawl for source ingestion). These are paid tooling relationships only — none of these vendors has any influence over editorial coverage.</p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

